import { Button, IconButton, Select } from "@conlit/ui";
import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { useAuth } from "@/features/authentication/auth-context";
import { BrandLogo } from "@/components/brand/brand-logo";
import { useLocale } from "@/features/localisation/locale-context";
import {
  clearRememberedParticipantSession,
  getRememberedParticipantSession,
  participantSessionIdFromPath,
  rememberParticipantSession,
} from "@/features/participants/participant-session";

const navClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-control px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-blue-soft text-primary" : "text-text-muted hover:bg-surface-disabled hover:text-text"}`;
export function SiteHeader() {
  const { locale, setLocale, t } = useLocale();
  const { principal, isDevelopmentAdapter, setDevelopmentPrincipal, signOut } = useAuth();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const routeSessionId = participantSessionIdFromPath(pathname);
  const participantSessionId = routeSessionId ?? getRememberedParticipantSession();
  const participantDashboardPath = participantSessionId
    ? `/play/${participantSessionId}/welcome`
    : "/join";
  const isParticipant = principal === "participant";
  const showClassroomNavigation = principal === "anonymous" || isParticipant;

  useEffect(() => {
    if (principal === "participant" && routeSessionId) {
      rememberParticipantSession(routeSessionId);
    }
  }, [principal, routeSessionId]);

  const leaveDevice = async () => {
    await signOut();
    clearRememberedParticipantSession();
  };
  const roleLink: readonly [string, string] | undefined =
    principal === "teacher"
      ? ["/teacher/dashboard", t("nav.teacher")]
      : principal === "researcher"
        ? ["/researcher/dashboard", t("nav.researcher")]
        : principal === "administrator"
          ? ["/admin/organisations", t("nav.admin")]
          : principal === "superadmin"
            ? ["/platform", t("nav.platform")]
            : undefined;
  return (
    <header className="border-border bg-surface border-b">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="mr-auto rounded-sm" aria-label="ConLit home">
          <BrandLogo />
        </Link>
        <nav aria-label={t("nav.primary")} className="hidden items-center gap-1 md:flex">
          <NavLink to="/constitution" className={navClass}>
            {t("nav.constitution")}
          </NavLink>
          <NavLink to="/about" className={navClass}>
            {t("nav.about")}
          </NavLink>
          {showClassroomNavigation && (
            <NavLink to={isParticipant ? participantDashboardPath : "/join"} className={navClass}>
              {isParticipant ? t("nav.studentDashboard") : t("nav.join")}
            </NavLink>
          )}
          {roleLink && (
            <NavLink to={roleLink[0]} className={navClass}>
              {roleLink[1]}
            </NavLink>
          )}
        </nav>
        <label className="text-text-muted hidden items-center gap-2 text-xs sm:flex">
          <span className="sr-only">{t("language.label")}</span>
          <Select
            aria-label={t("language.label")}
            className="w-auto min-w-24 py-1"
            value={locale}
            onChange={(event) => setLocale(event.target.value as "th" | "en")}
          >
            <option value="th">{t("language.thai")}</option>
            <option value="en">{t("language.english")}</option>
          </Select>
        </label>
        {isDevelopmentAdapter && (
          <label className="text-text-muted hidden items-center gap-2 text-xs lg:flex">
            Demo role
            <Select
              aria-label="Demo role"
              className="w-auto min-w-32 py-1"
              value={principal}
              onChange={(event) => setDevelopmentPrincipal(event.target.value as typeof principal)}
            >
              <option value="teacher">Teacher</option>
              <option value="researcher">Researcher</option>
              <option value="administrator">Administrator</option>
              <option value="superadmin">Superadmin</option>
              <option value="participant">Participant</option>
              <option value="anonymous">Signed out</option>
            </Select>
          </label>
        )}
        {principal === "anonymous" && (
          <Button asChild variant="secondary" size="sm" className="hidden md:inline-flex">
            <Link to="/login">{t("nav.signIn")}</Link>
          </Button>
        )}
        {!isDevelopmentAdapter && principal !== "anonymous" && (
          <Button
            variant="ghost"
            size="sm"
            className="hidden md:inline-flex"
            onClick={() => void (isParticipant ? leaveDevice() : signOut())}
          >
            {principal === "participant" ? t("nav.leaveDevice") : t("nav.signOut")}
          </Button>
        )}
        <IconButton
          className="md:hidden"
          variant="ghost"
          icon={mobileOpen ? X : Menu}
          label={mobileOpen ? t("nav.close") : t("nav.open")}
          aria-expanded={mobileOpen}
          aria-controls="mobile-primary-navigation"
          onClick={() => setMobileOpen((open) => !open)}
        />
      </div>
      {mobileOpen && (
        <nav
          id="mobile-primary-navigation"
          aria-label={t("nav.mobile")}
          className="border-border bg-surface grid gap-1 border-t px-4 py-4 md:hidden"
        >
          <NavLink to="/constitution" className={navClass} onClick={() => setMobileOpen(false)}>
            {t("nav.constitution")}
          </NavLink>
          <NavLink to="/about" className={navClass} onClick={() => setMobileOpen(false)}>
            {t("nav.about")}
          </NavLink>
          {showClassroomNavigation && (
            <NavLink
              to={isParticipant ? participantDashboardPath : "/join"}
              className={navClass}
              onClick={() => setMobileOpen(false)}
            >
              {isParticipant ? t("nav.studentDashboard") : t("nav.join")}
            </NavLink>
          )}
          {roleLink && (
            <NavLink to={roleLink[0]} className={navClass} onClick={() => setMobileOpen(false)}>
              {roleLink[1]}
            </NavLink>
          )}
          <label className="text-text-muted mt-2 flex items-center gap-2 px-3 text-xs sm:hidden">
            {t("language.label")}
            <Select
              aria-label={`${t("language.label")} mobile`}
              className="w-auto min-w-28"
              value={locale}
              onChange={(event) => setLocale(event.target.value as "th" | "en")}
            >
              <option value="th">{t("language.thai")}</option>
              <option value="en">{t("language.english")}</option>
            </Select>
          </label>
          {isDevelopmentAdapter && (
            <label className="text-text-muted mt-2 flex items-center gap-2 px-3 text-xs">
              Demo role
              <Select
                aria-label="Demo role mobile"
                className="w-auto min-w-32"
                value={principal}
                onChange={(event) =>
                  setDevelopmentPrincipal(event.target.value as typeof principal)
                }
              >
                <option value="teacher">Teacher</option>
                <option value="researcher">Researcher</option>
                <option value="administrator">Administrator</option>
                <option value="superadmin">Superadmin</option>
                <option value="participant">Participant</option>
                <option value="anonymous">Signed out</option>
              </Select>
            </label>
          )}
          {principal === "anonymous" && (
            <Button asChild variant="secondary" className="mt-2">
              <Link to="/login" onClick={() => setMobileOpen(false)}>
                {t("nav.signIn")}
              </Link>
            </Button>
          )}
          {!isDevelopmentAdapter && principal !== "anonymous" && (
            <Button
              variant="ghost"
              className="mt-2"
              onClick={() => {
                void (isParticipant ? leaveDevice() : signOut());
                setMobileOpen(false);
              }}
            >
              {principal === "participant" ? t("nav.leaveDevice") : t("nav.signOut")}
            </Button>
          )}
        </nav>
      )}
    </header>
  );
}
